import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Loader2, AlertCircle, CheckCircle, Car, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { adminPlatesAPI, vehiclesAPI } from '../services/api';

const formatPlate = (raw) => {
  const clean = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/[IOU]/g, '').slice(0, 7);
  if (/^[A-Z]{2}/.test(clean)) {
    return [clean.slice(0, 2), clean.slice(2, 5), clean.slice(5, 7)].filter(Boolean).join('-');
  }
  return clean;
};

const selectClass = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white disabled:bg-gray-100 disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500';

const AdminPlatesPanel = () => {
  const [plates, setPlates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [form, setForm] = useState({ plate: '', brand: '', model: '', year: '', engine_name: '' });
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [engines, setEngines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const loadPlates = useCallback(async () => {
    try {
      const data = await adminPlatesAPI.getAll();
      setPlates(data.plates);
    } catch (error) {
      console.error('Error loading plates:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadPlates(); }, [loadPlates]);
  useEffect(() => { vehiclesAPI.getBrands().then(setBrands).catch(() => setBrands([])); }, []);

  useEffect(() => {
    setModels([]);
    if (form.brand) vehiclesAPI.getModels(form.brand).then(setModels).catch(() => setModels([]));
  }, [form.brand]);

  useEffect(() => {
    setYears([]);
    if (form.brand && form.model) vehiclesAPI.getYears(form.brand, form.model).then(setYears).catch(() => setYears([]));
  }, [form.brand, form.model]);

  useEffect(() => {
    setEngines([]);
    if (form.brand && form.model && form.year) {
      vehiclesAPI.getEngines(form.brand, form.model, parseInt(form.year)).then(setEngines).catch(() => setEngines([]));
    }
  }, [form.brand, form.model, form.year]);

  const update = (patch) => setForm(f => ({ ...f, ...patch }));

  const handleSubmit = async () => {
    setMessage(null);
    if (!form.plate || !form.brand || !form.model || !form.year) {
      setMessage({ type: 'error', text: 'Plaque, marque, modèle et année sont obligatoires' });
      return;
    }
    setSaving(true);
    try {
      const saved = await adminPlatesAPI.register({
        plate: form.plate,
        brand: form.brand,
        model: form.model,
        year: parseInt(form.year),
        engine_name: form.engine_name || null,
      });
      setMessage({ type: 'success', text: `Plaque ${saved.plate} liée à ${saved.brand} ${saved.model} (${saved.year})` });
      setForm({ plate: '', brand: '', model: '', year: '', engine_name: '' });
      await loadPlates();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de l\'enregistrement' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plate) => {
    if (!window.confirm(`Supprimer la plaque ${plate} ?`)) return;
    try {
      await adminPlatesAPI.delete(plate);
      await loadPlates();
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la suppression' });
    }
  };

  const visible = plates.filter(p =>
    (!statusFilter || (p.status || 'validated') === statusFilter) &&
    (!filter || [p.plate, p.brand, p.model, p.user_email].some(v => (v || '').toLowerCase().includes(filter.toLowerCase())))
  );
  const pendingCount = plates.filter(p => p.status === 'pending').length;

  const setStatus = async (plate, status) => {
    if (status === 'refused' && !window.confirm(`Refuser la plaque ${plate} ? Elle ne sera pas reconnue par la recherche publique.`)) return;
    try {
      await adminPlatesAPI.setStatus(plate, status);
      setMessage({ type: 'success', text: status === 'validated' ? `Plaque ${plate} validée` : `Plaque ${plate} refusée` });
      await loadPlates();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de la mise à jour du statut' });
    }
  };

  const STATUS_LABELS = { pending: ['En attente', 'bg-amber-100 text-amber-700'], validated: ['Validée', 'bg-green-100 text-green-700'], refused: ['Refusée', 'bg-red-100 text-red-700'] };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Registre des plaques ({plates.length})</h1>
          {pendingCount > 0 && (
            <button data-testid="plates-pending-count" onClick={() => setStatusFilter('pending')} className="mt-1 text-sm text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full hover:bg-amber-100">
              {pendingCount} plaque(s) client en attente de validation
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="plates-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-sm border rounded-md px-3 py-2 bg-white">
            <option value="">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="validated">Validées</option>
            <option value="refused">Refusées</option>
          </select>
          <Button variant="outline" size="sm" onClick={loadPlates} data-testid="plates-refresh">
            <RefreshCw size={14} className="mr-1" /> Actualiser
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-testid="plate-register-form">
        <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Car size={18} className="text-orange-500" /> Lier une plaque à un véhicule
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Plaque *</label>
            <Input
              data-testid="plate-form-plate"
              value={form.plate}
              onChange={(e) => update({ plate: formatPlate(e.target.value) })}
              placeholder="AA-123-BB"
              className="font-mono uppercase"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Marque *</label>
            <select data-testid="plate-form-brand" value={form.brand} onChange={(e) => update({ brand: e.target.value, model: '', year: '', engine_name: '' })} className={selectClass}>
              <option value="">Choisir</option>
              {brands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Modèle *</label>
            <select data-testid="plate-form-model" value={form.model} disabled={!form.brand} onChange={(e) => update({ model: e.target.value, year: '', engine_name: '' })} className={selectClass}>
              <option value="">Choisir</option>
              {models.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Année *</label>
            <select data-testid="plate-form-year" value={form.year} disabled={!form.model} onChange={(e) => update({ year: e.target.value, engine_name: '' })} className={selectClass}>
              <option value="">Choisir</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Motorisation</label>
            <select data-testid="plate-form-engine" value={form.engine_name} disabled={!form.year} onChange={(e) => update({ engine_name: e.target.value })} className={selectClass}>
              <option value="">Première disponible</option>
              {engines.map(e => <option key={e.name} value={e.name}>{e.name} ({e.power}ch)</option>)}
            </select>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between gap-4">
          {message ? (
            <p data-testid="plate-form-message" className={`text-sm flex items-center gap-2 ${message.type === 'error' ? 'text-red-600' : 'text-green-700'}`}>
              {message.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
              {message.text}
            </p>
          ) : <span />}
          <Button data-testid="plate-form-submit" onClick={handleSubmit} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Plus size={16} className="mr-2" />}
            Enregistrer la plaque
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
        <div className="p-4 border-b">
          <Input
            data-testid="plates-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filtrer par plaque, marque ou modèle..."
            className="max-w-sm"
          />
        </div>
        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-orange-500" /></div>
        ) : (
          <table className="w-full min-w-[860px]" data-testid="plates-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plaque</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Véhicule</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Année</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Motorisation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {visible.map(p => (
                <tr key={p.plate} className="hover:bg-gray-50" data-testid={`plate-row-${p.plate}`}>
                  <td className="px-6 py-3 text-sm font-mono font-semibold text-gray-900">{p.plate}</td>
                  <td className="px-6 py-3 text-sm text-gray-900">{p.brand} {p.model}</td>
                  <td className="px-6 py-3 text-sm text-gray-600">{p.year}</td>
                  <td className="px-6 py-3 text-sm text-gray-600">{p.engine_name || '—'}</td>
                  <td className="px-6 py-3">
                    <span data-testid={`plate-source-${p.plate}`} className={`px-2 py-1 rounded-full text-xs font-medium ${p.source === 'client' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                      {p.source === 'client' ? 'Client' : 'Admin'}
                    </span>
                    {p.source === 'client' && p.user_email && (
                      <span className="block text-[11px] text-gray-500 mt-1" data-testid={`plate-user-${p.plate}`}>{p.user_email}</span>
                    )}
                  </td>
                  <td className="px-6 py-3">
                    <span data-testid={`plate-status-${p.plate}`} className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_LABELS[p.status || 'validated'][1]}`}>
                      {STATUS_LABELS[p.status || 'validated'][0]}
                    </span>
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-1">
                      {p.source === 'client' && p.status !== 'validated' && (
                        <button data-testid={`plate-validate-${p.plate}`} onClick={() => setStatus(p.plate, 'validated')} className="px-2 py-1 text-xs text-white bg-green-600 hover:bg-green-700 rounded">
                          Valider
                        </button>
                      )}
                      {p.source === 'client' && p.status !== 'refused' && (
                        <button data-testid={`plate-refuse-${p.plate}`} onClick={() => setStatus(p.plate, 'refused')} className="px-2 py-1 text-xs text-red-700 bg-red-50 hover:bg-red-100 rounded">
                          Refuser
                        </button>
                      )}
                      <button
                        data-testid={`plate-edit-${p.plate}`}
                        onClick={() => { setForm({ plate: p.plate, brand: p.brand, model: p.model, year: String(p.year), engine_name: p.engine_name || '' }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
                      >
                        {p.source === 'client' ? 'Corriger' : 'Modifier'}
                      </button>
                      <button data-testid={`plate-delete-${p.plate}`} onClick={() => handleDelete(p.plate)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!isLoading && visible.length === 0 && (
          <p className="text-center py-8 text-gray-500" data-testid="plates-empty">Aucune plaque enregistrée</p>
        )}
      </div>
    </>
  );
};

export default AdminPlatesPanel;
